import { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useChargePoints, useChargerCommands, useUsableIdTags } from '../lib/queries';
import { CONNECTOR_LABEL, chargePointLabel, chargePointTone, connectorTone } from '../data/types';
import type { ChargePoint, Connector } from '../data/types';
import { Loading, Pill } from '../components/ui';
import { errorText } from '../lib/errors';

/*
 * Start and stop a charge from the console.
 *
 * The reason this exists as its own thing rather than a button on a row: what
 * you are testing is a round trip through hardware, and the interesting part is
 * the several seconds *after* the click. A command is accepted by the CSMS
 * immediately and means almost nothing on its own — the charger then answers
 * with StatusNotification and StartTransaction, or does not, and that gap is
 * the whole diagnostic. So the panel stays open and watches.
 *
 * Nothing here ever reports "charging" because a command was accepted. It
 * reports what the charge point is actually saying.
 */

const RESULT_MESSAGES: Record<string, string> = {
  id_tag_required: 'Choose an idTag to start with.',
  id_tag_too_long: 'That idTag is longer than OCPP allows.',
  bad_connector_id: 'Pick a connector.',
  never_connected: 'This charge point has never connected, so there is nothing to send to.',
  not_online: 'This charge point is not connected right now. A command would go nowhere.',
  charge_point_blocked: 'This charge point is blocked. Unblock it before testing.',
  bad_transaction_id: 'That session has no numeric transaction id yet.',
};

/**
 * OCPP stop reasons, said in words that point at the fix.
 *
 * DeAuthorized is the one that matters on a test rig: the charge point sends
 * StartTransaction, the CSMS refuses the idTag in its response, and MicroOcpp
 * ends the session immediately. On screen that is a charge which flashes to
 * Charging and vanishes, with nothing anywhere saying the tag was the problem.
 */
const STOP_REASON: Record<string, string> = {
  DeAuthorized: 'the CSMS refused that idTag, so the charger stopped immediately',
  EVDisconnected: 'the cable was unplugged',
  Remote: 'stopped from here',
  Local: 'stopped at the charger',
  PowerLoss: 'the charge point lost power',
  Reboot: 'the charge point restarted',
  EmergencyStop: 'the emergency stop was pressed',
  HardReset: 'a hard reset was sent',
  SoftReset: 'a soft reset was sent',
  UnlockCommand: 'the connector was unlocked remotely',
};

/*
 * What the charger is doing, in the order it happens.
 *
 * The middle step used to be "acknowledged (Preparing)", lit whenever the
 * connector was in any state but available or offline. That was wrong twice
 * over: it stayed lit from the *previous* session's Finishing state, and on a
 * unit with a tethered cable — charge_station_01 among them — Preparing is the
 * resting state, so it was already true before the command was sent. A step
 * that is green before you press the button measures nothing.
 *
 * Each step now compares against a snapshot taken at the moment of sending, so
 * it can only go green because of *this* command.
 */
const SEQUENCE = [
  { key: 'sent', label: 'Command accepted by the CSMS' },
  { key: 'transaction', label: 'Charger opened a transaction' },
  { key: 'charging', label: 'Connector reporting Charging' },
];

export function TestBench() {
  // Fast polling: the point of the screen is watching a charger react.
  const { data: res, isLoading } = useChargePoints(true);
  const { data: tags } = useUsableIdTags();
  const { start, stop, setConfig, closeLost } = useChargerCommands();

  const points = useMemo(() => res?.items ?? [], [res]);
  /* Arriving from a charge point's Test button: that unit is already chosen,
     because picking it again is a step the operator has just taken. */
  const [params] = useSearchParams();
  const asked = params.get('cp');
  const [cpId, setCpId] = useState<string>('');
  const [connectorId, setConnectorId] = useState<number | null>(null);
  const [idTag, setIdTag] = useState('');
  const [note, setNote] = useState<string | null>(null);
  /* Seconds left before a forgotten session closes itself, and the operator's
     veto over that. The countdown is the confirmation: it says what is about to
     happen while there is still time to stop it. */
  const [autoIn, setAutoIn] = useState<number | null>(null);
  const [autoOff, setAutoOff] = useState(false);
  /* A charge point the server has answered connector_status_unreliable for.
     The served `trustsConnectorStatus` flag normally says this up front; this
     is the belt to its braces, so that one refusal is enough to stop the screen
     offering the close again — on 2026-10-23 the countdown re-armed against
     Charger A and drew the same 409 over and over. */
  const [unreliableCp, setUnreliableCp] = useState<string | null>(null);
  const [sentAt, setSentAt] = useState<number | null>(null);
  /* What was true when the command went out. Progress is measured against this
     rather than against absolute state, so nothing lights up because of a
     session that was already running. */
  const [baseline, setBaseline] = useState<string[] | null>(null);

  // Default to something that could actually work, rather than the first row.
  useEffect(() => {
    if (cpId || !points.length) return;
    if (asked && points.some((p) => p.id === asked)) { setCpId(asked); return; }
    const online = points.find((p) => p.presence === 'online' && !p.blocked);
    if (online) setCpId(online.id);
  }, [points, cpId, asked]);

  const cp: ChargePoint | undefined = points.find((p) => p.id === cpId);

  useEffect(() => {
    if (!cp) return;
    setConnectorId((prev) =>
      prev != null && cp.connectors.some((c) => c.id === prev)
        ? prev
        : cp.connectors[0]?.id ?? null);
  }, [cp]);

  if (isLoading) return <Loading />;

  const connector: Connector | undefined = cp?.connectors.find((c) => c.id === connectorId);
  const live = cp?.activeTransactions ?? [];

  /*
   * Does this session belong to the connector on screen?
   *
   * Matched on the id, with one concession: a CSMS row can carry no connectorId
   * at all, and on a single-connector charge point that can only have been the
   * one connector. On a multi-gun station it is genuinely unknown, so it is
   * left unclaimed rather than pinned to whichever gun is selected.
   *
   * This used to fall back to `live[0]` — the first open session on the whole
   * charge point. With gun 1 charging, selecting gun 2 showed gun 1's
   * transaction as gun 2's and refused to start anything, so a two-gun station
   * could only ever be driven one gun at a time from this screen.
   */
  const belongsHere = (t: { connectorId: number | null }) =>
    t.connectorId === connectorId
    || (t.connectorId == null && (cp?.connectors.length ?? 0) <= 1);

  const onThisConnector = live.find(belongsHere);

  /* The statuses that mean a session is actually running on the bay. Anything
     else — available, preparing, reserved, out of service, faulted, offline —
     is the charger saying it has no transaction, whatever the CSMS believes.
     Finishing counts as running: the transaction is stopping and the row stays
     open until the handshake completes, which is not a disagreement. */
  const IN_SESSION = ['charging', 'suspended', 'finishing'];

  /* A session the CSMS holds open on a connector the charger does not think is
     in one. They can only disagree if the charger lost it — a reset or a power
     cut mid-charge — and that reads very differently from a bay with a car in
     it. Checked against what the charger reports rather than a list of "free"
     statuses, so a cable plugged into a forgotten session (Preparing, which is
     what a re-plug after a reset looks like) is still caught. */
  /* Some hardware cannot be read this way at all.
     The YCL reports its connector `Available` while online with a transaction
     open and a car charging — the status timestamp is later than the session
     start, so it is not a stale snapshot, it is what the unit thinks. On a
     model like that an open transaction IS the session, and calling it a
     disagreement would invite an operator to end a live charge. The server
     decides this from one policy table and says so here. */
  const readsConnector = cp?.trustsConnectorStatus !== false && unreliableCp !== cpId;

  const stale = readsConnector && !!onThisConnector && !!connector
    && !IN_SESSION.includes(connector.status);

  /* A charge point that is away has not lost the session — it cannot say
     anything about it either way, and its last connector status is a snapshot
     of the moment it vanished.

     The automatic close read that snapshot as "not in a session" and settled
     the transaction, so a car charging through a mobile blackspot was closed
     out at whatever the last meter value said — often zero. The server refuses
     it now; this stops the countdown from starting, so the screen does not
     promise something that will be rejected. */
  const away = !!cp && cp.presence !== 'online';

  /*
   * Age is what separates a forgotten session from one that has only just
   * begun.
   *
   * The CSMS creates the transaction row the moment the charger answers
   * StartTransaction, and the connector's StatusNotification arrives
   * separately, a moment later. In between there is an open session on a
   * connector still reporting Preparing — indistinguishable from a ghost, and
   * about as long-lived as the countdown below. Closing on that race would
   * destroy a session that was working perfectly.
   *
   * A session the charger has genuinely lost is minutes old at least, so the
   * clock only runs once this one is too old to be that race.
   */
  const GHOST_MIN_AGE_MS = 60_000;
  const sessionAgeMs = onThisConnector?.startTime
    ? Date.now() - Date.parse(onThisConnector.startTime) : null;
  const oldEnough = sessionAgeMs != null && sessionAgeMs >= GHOST_MIN_AGE_MS;
  const ghostId = stale ? onThisConnector!.transactionId : null;

  const usable = cp?.presence === 'online' && !cp.blocked;

  /* Whether the tag typed is one the CSMS would accept. Checked before the
     command goes out: the alternative is watching a session die a second later
     and having to read OCPP frames to find out why. */
  const chosen = (tags ?? []).find((t) => t.tag === idTag.trim()) ?? null;
  const known = !!chosen;
  const unknownTag = idTag.trim().length > 0 && !!tags && !known;

  /* A refusal that landed after we sent.
     Read from the StartTransaction exchange, not from the Transactions table:
     the CSMS never creates a transaction row for a start it rejected, so there
     was nothing there to find and the screen stayed silent about it. Only
     counted when newer than the command, so a stale refusal from yesterday
     does not accuse this attempt. */
  const refused = cp?.lastStart
    && !cp.lastStart.accepted
    && sentAt != null
    && cp.lastStart.at != null
    && Date.parse(cp.lastStart.at) >= sentAt - 5000
    ? cp.lastStart : null;
  const why = !cp ? 'Choose a charge point.'
    : cp.blocked ? 'This charge point is blocked.'
      : cp.presence === 'never' ? 'This charge point has never connected.'
        : cp.presence !== 'online' ? 'This charge point is not connected right now.'
          : null;

  const say = (e: Error) => RESULT_MESSAGES[e.message] ?? errorText(e);

  /* Where the charger has got to. Read from what it is reporting, never from
     what we sent — a command that was accepted and then ignored has to look
     different from one that worked, or the screen is useless. */
  /* Scoped to this connector as well: otherwise a session starting on the other
     gun lights up the progress steps for the gun being watched. */
  const fresh = baseline == null
    ? undefined
    : live.find((t) => belongsHere(t) && !baseline.includes(t.transactionId));
  const reached = (key: string) => {
    if (key === 'sent') return sentAt != null;
    if (key === 'transaction') return !!fresh;
    return !!fresh && connector?.status === 'charging';
  };

  const elapsed = sentAt ? Math.round((Date.now() - sentAt) / 1000) : 0;

  /** Close a session the charger has forgotten. Shared by the button and the
   *  countdown, so both report the outcome the same way. */
  const closeGhost = (transactionId: string, automatic: boolean, force = false) => {
    closeLost.mutate({ cpId, transactionId, force }, {
      onSuccess: (r) => setNote(
        `${automatic ? 'Automatically closed' : 'Closed'} session ${r.transactionId}, `
        + `settled at ${r.totalKwh} kWh.`
        + (r.anomaly ? ` Flagged: ${r.anomaly}.` : '')),
      onError: (e: Error) => {
        /* A refusal is an answer, not a glitch to retry. Every one of these
           codes will come back the same on the next attempt, so the countdown
           stands down for this session and the operator reads why. */
        setAutoOff(true);
        if (e.message === 'connector_status_unreliable') setUnreliableCp(cpId);
        setNote(
          e.message === 'charger_still_running'
            ? 'The charger now says this bay is in a session, so it was not closed.'
            : e.message === 'connector_status_unreliable'
              ? 'Not closed, and it will not be retried. This model reports a '
                + 'connector as Available while a session is running on it, so '
                + 'that is no evidence the session is over — the car may still be '
                + 'charging. It stays open until the charger stops it. If the '
                + 'charger reboots and forgets it, the server notices from the '
                + 'meter values and closes it by itself.'
              : errorText(e, {
                charger_offline: 'The charge point is not connected, so it cannot say '
                  + 'whether this session is still running. It stays open until it returns.',
                session_held: 'This session is being held open through an outage. It '
                  + 'will be settled by itself if the charger comes back having forgotten it.',
              }));
      },
    });
  };

  /* Re-arm for a different session: a veto applies to the ghost it was aimed
     at, not to every ghost thereafter. */
  useEffect(() => { setAutoOff(false); }, [ghostId]);

  useEffect(() => {
    /* `away` is the new one: never close automatically while the charge point
       is off the network. That is the case the session watcher holds open. */
    if (!ghostId || !oldEnough || autoOff || away || closeLost.isPending) {
      setAutoIn(null);
      return;
    }
    let left = 10;
    setAutoIn(left);
    const tick = setInterval(() => { left -= 1; setAutoIn(left > 0 ? left : 0); }, 1000);
    const fire = setTimeout(() => {
      clearInterval(tick);
      setAutoIn(null);
      closeGhost(ghostId, true);
    }, 10_000);
    return () => { clearInterval(tick); clearTimeout(fire); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ghostId, oldEnough, autoOff, away, cpId]);

  return (
    <>
      <div className="card">
        <div className="li-title">Start a charge</div>
        <div className="li-sub" style={{ marginBottom: 12 }}>
          Sends RemoteStartTransaction to a charger, the same call the driver app
          will make. The command goes through the CSMS, which owns the websocket.
        </div>

        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
          <div style={{ flex: '2 1 220px', minWidth: 0 }}>
            <label className="li-sub" htmlFor="tb-cp">Charge point</label>
            <select id="tb-cp" className="field" value={cpId}
                    onChange={(e) => {
                      setCpId(e.target.value); setSentAt(null); setBaseline(null); setNote(null);
                    }}>
              <option value="">- choose -</option>
              {points.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name} - {chargePointLabel(p)}
                </option>
              ))}
            </select>
          </div>

          <div style={{ flex: '1 1 150px', minWidth: 0 }}>
            <label className="li-sub" htmlFor="tb-conn">Connector</label>
            <select id="tb-conn" className="field" value={connectorId ?? ''}
                    disabled={!cp?.connectors.length}
                    onChange={(e) => {
                      /* The progress panel below describes one attempt on one
                         connector. Carrying it across a change of gun would
                         report the other gun's start as this one's. */
                      setConnectorId(Number(e.target.value));
                      setSentAt(null); setBaseline(null); setNote(null);
                    }}>
              {!cp?.connectors.length && <option value="">none reported</option>}
              {cp?.connectors.map((c) => (
                <option key={c.id} value={c.id}>
                  C{c.id}{c.type ? ` - ${c.type}` : ''} - {CONNECTOR_LABEL[c.status]}
                </option>
              ))}
            </select>
          </div>

          <div style={{ flex: '2 1 260px', minWidth: 0 }}>
            {/* Charging is something a *car* does. An idTag is how that reaches
                the charger, but nobody thinks in tags -- so the vehicle is the
                thing chosen and the tag follows from it. Free text stays,
                because a test rig has to be able to send an arbitrary one. */}
            <label className="li-sub" htmlFor="tb-vehicle">Charge as</label>
            <select
              id="tb-vehicle"
              className="field"
              value={(tags ?? []).some((t) => t.tag === idTag) ? idTag : ''}
              onChange={(e) => setIdTag(e.target.value)}
            >
              <option value="">— type a tag below —</option>
              {(tags ?? []).filter((t) => t.source === 'issued').map((t) => (
                <option key={t.tag} value={t.tag}>
                  {t.driverName} · {t.vehicleName}
                  {t.plate ? ` (${t.plate})` : ''}
                  {t.connector ? ` · ${t.connector}` : ''}
                </option>
              ))}
              {(tags ?? []).filter((t) => t.source === 'csms').map((t) => (
                <option key={t.tag} value={t.tag}>
                  {t.tag} — card in the CSMS, no driver
                </option>
              ))}
            </select>
          </div>

          <div style={{ flex: '1 1 180px', minWidth: 0 }}>
            <label className="li-sub" htmlFor="tb-tag">idTag sent</label>
            <input id="tb-tag" className="field mono" value={idTag}
                   placeholder="SPK-... or a card"
                   onChange={(e) => setIdTag(e.target.value.toUpperCase())} />
          </div>
        </div>

        <div className="li-sub faint" style={{ marginTop: 6 }}>
          {chosen
            ? chosen.source === 'issued'
              ? `${chosen.driverName}'s ${chosen.vehicleName} will be billed for this session.`
              : `${chosen.tag} belongs to no driver, so this session cannot be billed to anyone.`
            : idTag.trim()
              ? 'A tag with no vehicle behind it — the session will run and bill nobody.'
              : 'Pick a vehicle, or type any tag. Either starts a charge.'}
          {(tags ?? []).some((t) => t.source === 'issued')
            ? ''
            : ' No vehicle has a working idTag yet — issue one from Drivers to bill a session to someone.'}
        </div>

        {why && (
          <div className="note" style={{ marginTop: 10 }}>
            <b>{why}</b> A command would be accepted by the CSMS and then go
            nowhere, which looks exactly like the charger ignoring you.
          </div>
        )}

        {unknownTag && (
          <div className="note" style={{ marginTop: 10 }}>
            <b>The CSMS does not hold <span className="mono">{idTag.trim()}</span> yet.</b>{' '}
            Starting will authorize it first, because an operator sending a start
            is the authorization — without that the charger opens a transaction,
            the CSMS refuses the tag in its response, and it stops a second later.
            <div className="li-sub" style={{ marginTop: 6 }}>
              It becomes a credential that works on every charge point on the
              network until revoked, and it is recorded in the activity log. To
              give a tag to a real driver instead, add a vehicle on Drivers.
            </div>
          </div>
        )}

        {!why && onThisConnector && !stale && (
          <div className="note" style={{ marginTop: 10 }}>
            <b>Connector {connectorId} is busy.</b> Transaction{' '}
            <span className="mono">{onThisConnector.transactionId}</span> is open
            on it, so it cannot start another — stop that one first, or pick a
            different connector.
            {/* Said instead of offering a close. On this hardware the connector
                reading cannot tell a forgotten session from a live one, so the
                console has nothing to base a close on — and the server would
                refuse it anyway. */}
            {!readsConnector && (
              <div className="li-sub" style={{ marginTop: 6 }}>
                This model{cp?.model ? <> (<span className="mono">{cp.model}</span>)</> : ''} reports
                its connector as Available even while a car is charging, so the
                connector status says nothing about whether this session is
                still running, and the console does not offer to close it. If
                the charger reboots and forgets the session, the server notices
                from its meter values and closes it by itself. To end a charge
                that is really running, use Stop charging.
              </div>
            )}
          </div>
        )}

        {/* The charger says the bay is free while the CSMS still holds a session
            on it. Calling that "busy" describes the record rather than the
            hardware, and sends the operator looking for a car that is not
            there. */}
        {!why && stale && (
          <div className="note" style={{ marginTop: 10 }}>
            <b>The charger and the CSMS disagree about connector {connectorId}.</b>{' '}
            The charger reports it <b>{CONNECTOR_LABEL[connector!.status]}</b>, but
            the CSMS still has transaction{' '}
            <span className="mono">{onThisConnector!.transactionId}</span> open on
            it.
            <div className="li-sub" style={{ marginTop: 6 }}>
              That is a session the charger has forgotten — normally a reset or a
              power cut while it was running. Stop charging sends a
              RemoteStopTransaction, and the charger will likely reject it,
              having no such transaction to stop. Until the session is closed,
              this connector cannot start another.
            </div>

            {/* The distinction the whole session watcher exists for. A charge
                point that is away has not forgotten anything — it cannot say
                either way — and the car may still be charging. Closing on that
                settles a live charge at its last reported figure. */}
            {away && (
              <div className="li-sub" style={{ marginTop: 8 }}>
                <b>This charge point is not connected right now,</b> so it cannot
                say whether the session is still running. It is held open until it
                comes back: if it returns still charging, the session simply
                carries on; if it returns having forgotten, it is settled by
                itself. Nothing closes automatically while it is away.
              </div>
            )}

            {/* The only action that resolves this state, and it runs itself.
                The countdown is the confirmation: it says what is about to
                happen while there is still time to stop it. */}
            <div style={{ display: 'flex', gap: 8, marginTop: 10, flexWrap: 'wrap' }}>
              <button
                className="btn sm"
                disabled={closeLost.isPending}
                /* Forced only when the charge point is away, and only from a
                   deliberate click — the operator has been told above what
                   they are overriding. Nothing automatic ever forces. */
                onClick={() => {
                  if (away && !window.confirm(
                    'This charge point is offline, so it cannot say whether the '
                    + 'session is still running. Closing it now settles the '
                    + 'session at its last reported reading, even if the car is '
                    + 'still charging. Close it anyway?')) return;
                  closeGhost(onThisConnector!.transactionId, false, away);
                }}>
                {closeLost.isPending
                  ? 'Closing…'
                  : away
                    ? `Force close session ${onThisConnector!.transactionId}`
                    : autoIn != null
                      ? `Closing session ${onThisConnector!.transactionId} in ${autoIn}s — close now`
                      : `Close session ${onThisConnector!.transactionId} at the CSMS`}
              </button>
              {autoIn != null && !closeLost.isPending && (
                <button className="btn sm ghost" onClick={() => setAutoOff(true)}>
                  Leave it open
                </button>
              )}
            </div>

            <div className="li-sub" style={{ marginTop: 6 }}>
              {autoOff
                ? 'Left open. Close it by hand whenever you are ready.'
                : oldEnough
                  ? 'Settles it at the last meter value the charger reported, ends it '
                    + 'there, and frees the connector. The charge point is not contacted '
                    + '— it has nothing to tell.'
                  : 'Not closing this one automatically yet: a session under a minute '
                    + 'old may simply be one whose StatusNotification has not landed. '
                    + 'Close it by hand if you know it is lost.'}
            </div>
          </div>
        )}

        <div style={{ display: 'flex', gap: 8, marginTop: 14, flexWrap: 'wrap' }}>
          <button
            className="btn"
            /* A connector already running a transaction cannot start another.
               Leaving this enabled sent a second RemoteStartTransaction into a
               busy bay, which the charger answers by rejecting — a failure the
               console caused and then reported as the charger's. */
            disabled={!usable || !idTag.trim() || start.isPending || !!onThisConnector}
            title={onThisConnector
              ? `Connector ${connectorId} is already running transaction ${onThisConnector.transactionId}.`
              : undefined}
            onClick={() => {
              setNote(null);
              start.mutate(
                {
                  cpId, idTag: idTag.trim(), connectorId: connectorId ?? undefined,
                  // The console is a trusted surface: what it sends, it grants.
                  authorizeUnknown: true,
                },
                {
                  onSuccess: (r) => {
                    setSentAt(Date.now());
                    setBaseline(live.map((t) => t.transactionId));
                    /* Drop the sampling interval as soon as a charge is
                       started. At the shipped 60s a short test reports no
                       energy at all, which looks exactly like a broken meter;
                       asking for 5s here means nobody has to remember to. */
                    if (r.accepted) {
                      setConfig.mutate({
                        cpId, key: 'MeterValueSampleInterval', value: 5,
                      });
                    }
                    setNote([
                      r.authorized
                        ? `Authorized ${r.authorized.tag}${r.authorized.created
                          ? ''
                          : ` (was ${r.authorized.previousStatus})`}.`
                        : null,
                      r.accepted
                        ? 'Sent. Watching for the charger to answer. Asked it to sample every 5s.'
                        : 'The CSMS did not accept it - see the response below.',
                    ].filter(Boolean).join(' '));
                  },
                  onError: (e: Error) => {
                    setSentAt(null); setBaseline(null); setNote(say(e));
                  },
                },
              );
            }}
          >
            {start.isPending ? 'Sending...' : 'Start charging'}
          </button>

          <button
            className="btn ghost"
            disabled={!onThisConnector || stop.isPending}
            title={onThisConnector
              ? `Stops transaction ${onThisConnector.transactionId} on connector ${onThisConnector.connectorId ?? connectorId}.`
              : 'Nothing is running on this connector.'}
            onClick={() => {
              setNote(null);
              stop.mutate(
                { cpId, transactionId: onThisConnector!.transactionId },
                {
                  onSuccess: (r) => setNote(r.accepted
                    ? 'Stop sent. Watching for the charger to close the session.'
                    : 'The CSMS did not accept the stop - see the response below.'),
                  onError: (e: Error) => setNote(say(e)),
                },
              );
            }}
          >
            {/* Always "Stop charging". The pair of buttons is Start/Stop and
                should read as a pair; which transaction it will stop is already
                stated in the busy note above, and putting the number on the
                button made the two verbs look like different kinds of thing. */}
            {stop.isPending ? 'Sending...' : 'Stop charging'}
          </button>

          {sentAt && (
            <button className="btn ghost sm"
                    onClick={() => { setSentAt(null); setBaseline(null); setNote(null); }}>
              Reset
            </button>
          )}
        </div>

        {note && <div className="li-sub" style={{ marginTop: 10 }}>{note}</div>}

        {/* Why a short test charge reports no energy. The charger samples its
            meter on MeterValueSampleInterval, which ships at 60s, so a session
            started and stopped inside a minute never gets asked. That is
            indistinguishable on screen from a meter that is not working. */}
        {cp && usable && (
          <div className="note" style={{ marginTop: 10 }}>
            <b>Energy only appears when the charger samples its meter.</b> That
            interval ships at 60 seconds, so a charge shorter than a minute
            reports nothing at all — the session worked, it was simply never
            asked. Shorten it while testing:
            <div style={{ display: 'flex', gap: 8, marginTop: 8, flexWrap: 'wrap' }}>
              {[5, 10, 30, 60].map((secs) => (
                <button
                  key={secs}
                  className="btn ghost sm"
                  disabled={setConfig.isPending}
                  onClick={() => setConfig.mutate(
                    { cpId, key: 'MeterValueSampleInterval', value: secs },
                    {
                      onSuccess: (r) => setNote(r.accepted
                        ? `Asked the charger to sample every ${secs}s. It applies to the next session.`
                        : 'The charger did not accept that configuration change.'),
                      onError: () => setNote('Could not send the configuration change.'),
                    },
                  )}
                >
                  every {secs}s
                </button>
              ))}
            </div>
          </div>
        )}

        {refused && (
          <div className="note" style={{ marginTop: 10 }}>
            <b>Refused. The CSMS answered <span className="mono">
              {refused.status ?? 'not Accepted'}
            </span> for idTag <span className="mono">{refused.idTag ?? '—'}</span>.</b>{' '}
            The charger opened a transaction, got that back, and stopped one
            second later with DeAuthorized. Nothing was charged and no session
            was recorded — a rejected start leaves no transaction at all, which
            is why the list below stays empty.
          </div>
        )}
      </div>

      {/* What the charger is actually doing. Refreshed every three seconds
          while this screen is open. */}
      {cp && (
        <div className="card" style={{ marginTop: 14 }}>
          <div className="row" style={{ justifyContent: 'space-between' }}>
            <div className="li-title">{cp.name}</div>
            <Pill tone={chargePointTone(cp)}>{chargePointLabel(cp)}</Pill>
          </div>
          <div className="li-sub mono" style={{ marginBottom: 10 }}>
            {cp.chargePointId}
            {cp.silentFor != null && ` · last message ${cp.silentFor}s ago`}
          </div>

          {sentAt != null && (
            <div className="tbl" style={{ marginBottom: 12 }}>
              <table>
                <tbody>
                  {SEQUENCE.map((step) => (
                    <tr key={step.key}>
                      <td style={{ width: 34 }}>
                        <span className={`dot ${reached(step.key) ? 'on' : 'off'}`} />
                      </td>
                      <td>{step.label}</td>
                      <td className="num mono li-sub">
                        {reached(step.key) ? 'yes' : `${elapsed}s`}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          <div className="conn-list">
            {cp.connectors.map((c) => (
              <div className="conn" key={c.id}>
                <span className="conn-id">C{c.id}</span>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div className="li-title">{c.label ?? c.type ?? `Connector ${c.id}`}</div>
                  <div className="li-sub">
                    {c.powerKw != null ? `${c.powerKw} kW` : 'power not reported'}
                    {c.current ? ` · ${c.current}` : ''}
                  </div>
                </div>
                <Pill tone={connectorTone(c.status)}>{CONNECTOR_LABEL[c.status]}</Pill>
              </div>
            ))}
            {!cp.connectors.length && (
              <div className="li-sub faint">
                This charge point has not reported any connectors.
              </div>
            )}
          </div>

          {cp.lastStart && !cp.lastStart.accepted && !refused && (
            <div className="li-sub" style={{ marginTop: 10, color: 'var(--bad)' }}>
              Last start attempt was refused ({cp.lastStart.status}) for idTag{' '}
              <span className="mono">{cp.lastStart.idTag ?? '—'}</span>
              {cp.lastStart.at && ` at ${new Date(cp.lastStart.at).toLocaleTimeString()}`}
            </div>
          )}

          {cp.lastStop && (
            <div className="li-sub" style={{ marginTop: 10 }}>
              Last session ended{' '}
              {cp.lastStop.at ? new Date(cp.lastStop.at).toLocaleTimeString() : ''}
              {cp.lastStop.reason
                ? ` — ${STOP_REASON[cp.lastStop.reason] ?? cp.lastStop.reason}`
                : ' — no reason given'}
              {cp.lastStop.kwh != null && ` · ${cp.lastStop.kwh.toFixed(3)} kWh`}
            </div>
          )}

          <div className="li-title" style={{ marginTop: 14, fontSize: 13 }}>Open sessions</div>
          {live.length ? (
            <div className="tbl" style={{ marginTop: 6 }}>
              <table>
                <thead>
                  <tr>
                    <th>Transaction</th><th className="num">Connector</th>
                    <th className="num">kWh</th><th>Meter</th><th>Connector</th><th>Started</th>
                  </tr>
                </thead>
                <tbody>
                  {live.map((t) => (
                    <tr key={t.transactionId}>
                      <td className="mono">{t.transactionId}</td>
                      <td className="num mono">{t.connectorId ?? '—'}</td>
                      {/* From the latest meter sample minus the reading the
                          session started on. Blank, not 0.00, before the first
                          sample arrives — a session two seconds old has not
                          been measured yet. */}
                      <td className="num mono">
                        {t.totalKwh == null
                          ? <span className="faint">—</span>
                          : Number(t.totalKwh).toFixed(3)}
                      </td>
                      <td className="li-sub">
                        {t.meterAt
                          ? `${Math.max(0, Math.round((Date.now() - Date.parse(t.meterAt)) / 1000))}s ago`
                          : <span className="faint">no sample yet</span>}
                      </td>
                      {/* chargingState is an OCPP 2.0.1 field and is null on
                          every 1.6 session, so this column could never say
                          anything. The connector's own state is the answer to
                          the question the column was asking. */}
                      <td>
                        {connector && t.connectorId === connector.id
                          ? CONNECTOR_LABEL[connector.status]
                          : <span className="faint">—</span>}
                      </td>
                      <td className="li-sub">
                        {t.startTime ? new Date(t.startTime).toLocaleTimeString() : '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="li-sub faint" style={{ marginTop: 6 }}>
              Nothing open on this charge point.
            </div>
          )}
        </div>
      )}

      {(start.data || stop.data) && (
        <div className="card" style={{ marginTop: 14 }}>
          <div className="li-title" style={{ fontSize: 13 }}>What the CSMS said</div>
          <div className="li-sub" style={{ marginBottom: 6 }}>
            Passed through untouched. When a charger refuses something, the reason
            is usually in here and nowhere else.
          </div>
          <pre className="mono" style={{
            fontSize: 11, overflow: 'auto', margin: 0,
            background: 'var(--bg2, transparent)', padding: 8, borderRadius: 6,
          }}>
            {JSON.stringify((stop.data ?? start.data)?.raw, null, 2)}
          </pre>
        </div>
      )}
    </>
  );
}
